// pageParts.js - HTML pieces shared by the city pages (CityPageGenerator.js) and the guide pages (SeoPageGenerator.js).
// Styles: public/city-page.css (prefix lx-). Behaviour: public/city-page.js.
import { escapeHtml as e } from './guidesSite';

export const IOS_URL = 'https://apps.apple.com/app/6748287909';
export const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.brandly.aitourguid';
export const GOOGLE_TAG = 'GT-WFM3P7S4'; // same tag as the WordPress pages (Site Kit)

export const ICON_PLAY = '<svg viewBox="0 0 24 24" aria-hidden="true"><path class="i-play" d="M8 5.5v13l11-6.5z"/><g class="i-pause"><rect x="6.5" y="5" width="4" height="14" rx="1"/><rect x="13.5" y="5" width="4" height="14" rx="1"/></g></svg>';
const APPLE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M16.4 12.6c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.8-1.6 0-3.1 1-4 2.4-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3 2.4 1.2 0 1.7-.8 3.1-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8 0 0-2.5-1-2.5-3.9zM14 5.4c.7-.8 1.1-1.9 1-3-1 0-2.1.7-2.8 1.5-.6.7-1.2 1.8-1 2.9 1 .1 2.1-.6 2.8-1.4z"/></svg>';
const PLAY_STORE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4.2 2.4c-.3.3-.4.7-.4 1.2v16.8c0 .5.1.9.4 1.2l9.3-9.6-9.3-9.6zm10.6 8.3 2.7-2.8L6.1 1.4l8.7 9.3zm0 2.6-8.7 9.3 11.4-6.5-2.7-2.8zm3.9-4.6-3 3 3 3 3.5-2c1-.6 1-1.4 0-2l-3.5-2z"/></svg>';

/** App Store and Google Play buttons. `page` names the analytics event: download_ios_<page>_<where>. */
export const stores = (where, dark = false, page = 'city') => {
  const c = dark ? ' lx-store-dark' : '';
  return `<div class="lx-stores">`
    + `<a class="lx-store${c}" href="${IOS_URL}" target="_blank" rel="noopener" data-gs-event="download_ios_${page}_${where}">${APPLE}<span><small>Download on the</small>App Store</span></a>`
    + `<a class="lx-store lx-store-ghost${c}" href="${e(ANDROID_URL)}" target="_blank" rel="noopener" data-gs-event="download_android_${page}_${where}">${PLAY_STORE}<span><small>Get it on</small>Google Play</span></a>`
    + `</div>`;
};

/** Round play button with progress ring; city-page.js plays data-src in one shared audio element. */
export const player = ({ url, label, small, aria }, cls = '') =>
  `<button type="button" class="lx-play ${cls}" data-src="${e(url)}" aria-label="${e(aria || label)}">`
  + `<span class="lx-play-ring">${ICON_PLAY}</span><span class="lx-play-txt"><b>${e(label)}</b><small><span class="lx-time">Stop 1</span>${small ? ` · ${e(small)}` : ''}</small></span></button>`;

/** Cut at a word and add an ellipsis. */
export const shorten = (text, max = 170) => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return t.slice(0, t.lastIndexOf(' ', max)).replace(/[,;:.\s]+$/, '') + '…';
};

/**
 * Whole sentences up to `max` characters. A trailing piece without a full stop is left out
 * (unless it is all there is), so a teaser never ends mid-word.
 */
export const sentences = (text, max = 220) => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  const parts = (t.match(/[^.!?]+(?:[.!?]+["'”’)]*|$)/g) || []).map(p => p.trim()).filter(Boolean);
  const whole = parts.filter(p => /[.!?]["'”’)]*$/.test(p));
  const usable = whole.length ? whole : parts;
  let out = '';
  for (const p of usable) {
    if (out && (out + ' ' + p).length > max) break;
    out = out ? `${out} ${p}` : p;
  }
  return shorten(out, Math.max(max, 60));
};

const SMALL_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'da', 'das', 'de', 'del', 'des', 'di', 'do', 'dos', 'du', 'e', 'el', 'em', 'en', 'for', 'in', 'la', 'le', 'na', 'no', 'of', 'on', 'or', 'the', 'to', 'y']);
// kept in capitals when a shouted title is tidied
const ACRONYMS = /^(gps|unesco|uk|usa|eu|ww(i|ii)|[ivx]{2,5})$/;
const isShouting = (s) => {
  const letters = s.replace(/[^\p{L}]/gu, '');
  return letters.length > 3 && letters === letters.toUpperCase() && letters !== letters.toLowerCase();
};

/**
 * Stop titles often come in capitals ("ESCADINHAS DE SÃO CRISTOVÃO: STAIRS THAT COUNT AS CARDIO").
 * A shouted part becomes Title Case, which keeps place names right ("The Uphill Battle to São Jorge Castle").
 */
export const tidyTitle = (title) => String(title || '').trim().split(/(:\s*)/).map(part => {
  if (!isShouting(part)) return part;
  let first = true;
  return part.toLowerCase().replace(/[\p{L}'’]+/gu, (w) => {
    const keep = !first && SMALL_WORDS.has(w);
    first = false;
    if (ACRONYMS.test(w)) return w.toUpperCase();
    return keep ? w : w.charAt(0).toUpperCase() + w.slice(1);
  });
}).join('');
